import { Box, useTheme } from "@mui/material"
import { Accordion } from "minimals-template/components/accordion"
import { useEffect, useRef, useState } from "react"

const COLLAPSE_SCROLL_THRESHOLD = 12
const TOGGLE_COOLDOWN_MS = 500

/**
 * Accordion that auto-collapses on downward scroll.
 * Scrolling back up does NOT re-expand it — only a manual toggle can expand it again.
 * Once the user manually expands or collapses the accordion, auto-toggling stops entirely.
 *
 * - Uses scroll direction, distance thresholds, and a short cooldown to avoid flicker.
 * - Anchors to the nearest scrollable parent (or window) and listens for its scroll events.
 * - Per-event scroll contribution is capped so a single layout-shift-induced scroll cannot
 *   trigger a toggle on its own.
 *
 * @param {object} props
 * @param {React.ReactNode} props.summaryContent Content to render inside the accordion summary.
 * @param {React.ReactNode} props.detailsContent Content to render inside the accordion details.
 */
export function AutoToggleAccordion({ summaryContent, detailsContent, sx, titleSx }) {
    const [expand, setExpand] = useState(true)
    const manualOverride = useRef(false)
    const containerRef = useRef(null)
    const autoState = useRef("expanded")
    const lastScrollTop = useRef(null)
    const scrollDirection = useRef(null)
    const accumulatedDelta = useRef(0)
    const cooldownUntil = useRef(0)

    const theme = useTheme()
    const defaultSx = {
        backgroundColor: theme.palette.grey[200],
    }
    sx = { ...defaultSx, ...sx }
    titleSx = { ...defaultSx, ...titleSx }

    useEffect(
        () => {
            const container = containerRef.current
            if (!container) return null
            const scrollable = findScrollableParent(container)

            autoState.current = expand ? "expanded" : "collapsed"
            scrollDirection.current = null
            accumulatedDelta.current = 0

            scrollable.addEventListener("scroll", onScroll, { passive: true })
            return function cleanup() {
                scrollable.removeEventListener("scroll", onScroll)
            }

            function onScroll() {
                const currentTop = getScrollTop(scrollable)
                if (manualOverride.current) return
                if (lastScrollTop.current === null) {
                    lastScrollTop.current = currentTop
                    return
                }

                const diff = currentTop - lastScrollTop.current
                lastScrollTop.current = currentTop

                if (Math.abs(diff) < 1) return

                if (Date.now() < cooldownUntil.current) return

                const direction = diff > 0 ? "down" : "up"
                if (direction !== scrollDirection.current) {
                    scrollDirection.current = direction
                    accumulatedDelta.current = 0
                }

                accumulatedDelta.current += Math.sign(diff) * Math.min(Math.abs(diff), COLLAPSE_SCROLL_THRESHOLD - 1)

                if (autoState.current !== "collapsed" && accumulatedDelta.current > COLLAPSE_SCROLL_THRESHOLD) {
                    autoState.current = "collapsed"
                    accumulatedDelta.current = 0
                    cooldownUntil.current = Date.now() + TOGGLE_COOLDOWN_MS
                    setExpand(false)
                }
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    )

    return (
        <Box sx={{ p: 2 }} ref={containerRef}>
            <Accordion
                defaultExpanded={true}
                open={expand}
                onChange={handleUserToggle}
                title={summaryContent}
                sx={sx}
                titleSx={titleSx}
                data-cy={`auto-toggle-accordion-${expand ? "expanded" : "collapsed"}`}
            >
                {detailsContent}
            </Accordion>
        </Box>
    )

    function getScrollTop(el) {
        return el === window
            ? window.scrollY ?? window.pageYOffset ?? document.documentElement.scrollTop ?? 0
            : el.scrollTop
    }

    function findScrollableParent(element) {
        let current = element
        while (current) {
            const style = getComputedStyle(current)
            const canScroll = /(auto|scroll)/.test(style.overflowY)
            if (canScroll && current.scrollHeight > current.clientHeight) {
                return current
            }
            current = current.parentElement
        }
        return window
    }

    function handleUserToggle(expanded) {
        manualOverride.current = true
        scrollDirection.current = null
        accumulatedDelta.current = 0
        autoState.current = expanded ? "expanded" : "collapsed"
        cooldownUntil.current = Date.now() + TOGGLE_COOLDOWN_MS
        setExpand(expanded)
    }
}
